import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { DomainEventUpdateItemDetailed } from '@/services/api/generated/matterCmsUpdatesService';
import type { DomainEventType } from '@/services/api/matterChangesService';

import { ChangeCard } from './ChangeCard.';
import { isEventSupported } from './events/EventRenderer';
import { UpdateRenderer } from './UpdateRenderer';

interface MatterUpdatesProps {
  updates: DomainEventUpdateItemDetailed[];
}

export const MatterUpdates = ({ updates }: MatterUpdatesProps) => {
  const [showFilteredEvents, setShowFilteredEvents] = useState(false);

  const { pendingUpdates, completedUpdates, filteredUpdates } = useMemo(() => {
    // Filter out events that don't have a renderer - they shouldn't be shown as CMS updates
    // Cast event to DomainEventType since matterCmsUpdatesService and matterChangesService
    // generate equivalent but structurally distinct types
    const supportedUpdates = updates.filter((update) =>
      isEventSupported(update.event as unknown as DomainEventType),
    );

    // Log unsupported pending events for debugging
    const unsupportedPending = updates.filter(
      (update) =>
        update.status === 'pending' &&
        !isEventSupported(update.event as unknown as DomainEventType),
    );
    if (unsupportedPending.length > 0) {
      console.warn(
        `[MatterUpdates] ${unsupportedPending.length} pending update(s) filtered out due to unsupported event types:`,
        unsupportedPending.map((u) => u.event.event_code),
      );
    }

    const pending = supportedUpdates.filter(
      (update) => update.status === 'pending',
    );
    const filtered = supportedUpdates.filter(
      (update) => update.status === 'filtered',
    );
    const completed = supportedUpdates.filter(
      (update) =>
        update.status === 'transferred' || update.status === 'skipped',
    );
    return {
      pendingUpdates: pending,
      completedUpdates: completed,
      filteredUpdates: filtered,
    };
  }, [updates]);

  const allCompletedUpdates = useMemo(() => {
    return [...completedUpdates, ...filteredUpdates];
  }, [completedUpdates, filteredUpdates]);

  return (
    <div className="flex flex-col gap-6">
      {/* Pending Tasks Section */}
      {pendingUpdates.length > 0 && (
        <ChangeCard
          title={`Pending Updates (${pendingUpdates.length})`}
          titleClassName="bg-gray-50 text-lg font-semibold text-gray-900"
        >
          <div className="flex flex-col gap-4">
            {pendingUpdates.map((update) => (
              <UpdateRenderer key={update.id} update={update} />
            ))}
          </div>
        </ChangeCard>
      )}

      {/* Completed Tasks Section */}
      {allCompletedUpdates.length > 0 && (
        <ChangeCard
          title={`Completed Updates (${allCompletedUpdates.length})`}
          titleClassName="bg-green-50 text-lg font-semibold text-green-900"
          titleAction={
            filteredUpdates.length > 0 && (
              <button
                type="button"
                onClick={() => setShowFilteredEvents(!showFilteredEvents)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                aria-label={
                  showFilteredEvents
                    ? 'Hide filtered events'
                    : 'Show filtered events'
                }
              >
                {showFilteredEvents ? (
                  <>
                    <EyeSlashIcon className="h-4 w-4" />
                    Hide Filtered
                  </>
                ) : (
                  <>
                    <EyeIcon className="h-4 w-4" />
                    Show Filtered ({filteredUpdates.length})
                  </>
                )}
              </button>
            )
          }
        >
          <div className="rounded-lg ">
            <div className="flex flex-col gap-4">
              {completedUpdates.map((update) => (
                <UpdateRenderer key={update.id} update={update} />
              ))}
              {showFilteredEvents &&
                filteredUpdates.map((update) => (
                  <UpdateRenderer key={update.id} update={update} />
                ))}
            </div>
          </div>
        </ChangeCard>
      )}

      {/* Empty State */}
      {pendingUpdates.length === 0 && allCompletedUpdates.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          No updates available
        </div>
      )}
    </div>
  );
};
